import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import {
  VALUATION_AGENT_REQUEST_KIND_VALUES,
  ValuationAgentRequestKindSchema,
} from './valuation-agent-request.schemas';

// The kinds of viewing a Bewertung 1b names (IMM-1397's capture, AUF.BESICHTIGUNG.ART; „keine Besichtigung“
// for a 1b without a completed capture, per the IMM-1398 design run).
export const VALUATION_VIEWING_KIND_VALUES = [
  'INTERIOR_AND_EXTERIOR',
  'EXTERIOR_ONLY',
  'PARTIAL',
  'VIDEO',
  'NONE',
] as const;
export const ValuationViewingKindSchema = z.enum(VALUATION_VIEWING_KIND_VALUES);
export type ValuationViewingKind = z.infer<typeof ValuationViewingKindSchema>;

// What the Objekt's value rests on (IMM-1391). A briefing is never the value, so it is not a source kind.
export const VALUATION_SOURCE_KIND_VALUES = ['OPERATOR', 'VALUATION_1A', 'VALUATION_1B'] as const;
export const ValuationSourceSchema = z.object({
  kind: z.enum(VALUATION_SOURCE_KIND_VALUES),
  viewingKind: ValuationViewingKindSchema.nullable(), // non-null iff VALUATION_1B
  withAssumptions: z.boolean(), // false for OPERATOR
});
export type ValuationSource = z.infer<typeof ValuationSourceSchema>;

// Every kind a valuation-history entry can have: the operator's (IMM-407) and the agent's three.
export const VALUATION_ENTRY_KIND_VALUES = ['OPERATOR', ...VALUATION_AGENT_REQUEST_KIND_VALUES] as const;
export const ValuationEntryKindSchema = z.enum(VALUATION_ENTRY_KIND_VALUES);
export type ValuationEntryKind = z.infer<typeof ValuationEntryKindSchema>;

export const VALUATION_AGENT_RESULT_CONTENT_TYPES = ['application/pdf'] as const;
export const VALUATION_AGENT_RESULT_MEDIA_DESCRIPTION = 'valuation-agent-result';

// numeric(14, 2) holds at most 999 999 999 999.99.
const Euro = z.number().positive().max(999_999_999_999);

const resultFigures = {
  mediaFileId: z.uuid(), // the confirmed upload from …/result-upload/confirm
  priceFrom: Euro,
  priceTo: Euro,
  recommendedStartPrice: Euro.nullable(),
  withAssumptions: z.boolean(), // „mit Annahmen“: the result rests on assumed values
};

export const DeliverValuationAgentResultBodySchema = z
  .discriminatedUnion('kind', [
    z.object({ kind: z.literal('ACQUISITION_BRIEFING'), ...resultFigures }).strict(),
    z.object({ kind: z.literal('VALUATION_1A'), ...resultFigures }).strict(),
    z.object({ kind: z.literal('VALUATION_1B'), ...resultFigures, viewingKind: ValuationViewingKindSchema }).strict(),
  ])
  .refine((body) => body.priceTo >= body.priceFrom, { path: ['priceTo'], message: 'priceTo must be ≥ priceFrom' });
export type DeliverValuationAgentResultBody = z.infer<typeof DeliverValuationAgentResultBodySchema>;

export const ValuationAgentResultSchema = z.object({
  id: z.uuid(),
  requestId: z.uuid(),
  kind: ValuationAgentRequestKindSchema,
  propertyId: refersTo('property', z.uuid()),
  dealId: refersTo('deal', z.uuid()).nullable(),
  priceFrom: z.number(),
  priceTo: z.number(),
  recommendedStartPrice: z.number().nullable(),
  withAssumptions: z.boolean(),
  viewingKind: ValuationViewingKindSchema.nullable(), // non-null iff VALUATION_1B
  documentId: refersTo('document', z.uuid()), // the PDF under „Interne Unterlagen“
  deliveredAt: z.iso.datetime(),
  supersededAt: z.iso.datetime().nullable(), // when a newer result of this kind for the Objekt arrived
});
export type ValuationAgentResult = z.infer<typeof ValuationAgentResultSchema>;

export const PropertyValuationAgentResultsPathParamsSchema = z.object({ organizationId: z.uuid(), id: z.uuid() });

export const PropertyValuationAgentResultsResponseSchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  // At most one per kind, in the order VALUATION_1B, VALUATION_1A, ACQUISITION_BRIEFING. A kind with no result is absent.
  current: z.array(ValuationAgentResultSchema),
  // Every earlier result, newest delivery first, each with supersededAt set.
  superseded: z.array(ValuationAgentResultSchema),
});
export type PropertyValuationAgentResultsResponse = z.infer<typeof PropertyValuationAgentResultsResponseSchema>;
